import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Briefcase, 
  CheckCircle, 
  XCircle, 
  Clock,
  Search,
  Filter,
  X,
  Save
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { Base_Url } from '../config/domainConfig';
import './BusinessCategoryMaster.css';

const API_BASE_URL = `${Base_Url}/api`;

const BusinessCategoryMaster = () => {
  const { showSuccess, showError } = useToast();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    categoryname: '',
    categorycode: '',
    description: '',
    status: 'active'
  });
  
  const [formErrors, setFormErrors] = useState({});

  // Fetch categories data
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/business-category-master`);
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCategories(Array.isArray(result.data) ? result.data : []);
        } else {
          showError('Failed to fetch categories: ' + result.message);
          setCategories([]);
        }
      } else {
        showError('Failed to fetch categories');
        setCategories([]);
      }
    } catch (error) {
      showError('Error fetching categories: ' + error.message);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Filter categories based on search and filters
  const filteredCategories = Array.isArray(categories) ? categories.filter(category => {
    const matchesSearch = category.categoryname.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         category.categorycode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || category.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  }) : [];

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.categoryname.trim()) {
      errors.categoryname = 'Category name is required';
    }
    
    if (!formData.categorycode.trim()) {
      errors.categorycode = 'Category code is required';
    } else if (formData.categorycode.length > 10) {
      errors.categorycode = 'Category code must be 10 characters or less';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle add category
  const handleAddCategory = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/business-category-master`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        showSuccess('Business category added successfully!');
        setShowAddModal(false);
        resetForm();
        fetchCategories();
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Failed to add business category');
      }
    } catch (error) {
      showError('Error adding business category: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle edit category
  const handleEditCategory = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/business-category-master/${selectedCategory.categoryid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        showSuccess('Business category updated successfully!');
        setShowEditModal(false);
        resetForm();
        fetchCategories();
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Failed to update business category');
      }
    } catch (error) {
      showError('Error updating business category: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete category
  const handleDeleteCategory = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/business-category-master/${selectedCategory.categoryid}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        showSuccess('Business category deleted successfully!');
        setShowDeleteModal(false);
        fetchCategories();
      } else {
        const errorData = await response.json();
        showError(errorData.message || 'Failed to delete business category');
      }
    } catch (error) {
      showError('Error deleting business category: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Open edit modal
  const openEditModal = (category) => {
    setSelectedCategory(category);
    setFormData({
      categoryname: category.categoryname,
      categorycode: category.categorycode,
      description: category.description || '',
      status: category.status
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (category) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      categoryname: '',
      categorycode: '',
      description: '',
      status: 'active'
    });
    setFormErrors({});
    setSelectedCategory(null);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'success',
      inactive: 'danger'
    };
    
    return (
      <span className={`status-badge ${statusClasses[status] || 'default'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="loading">
        <div>Loading business categories...</div>
      </div>
    );
  }

  return (
    <div className="business-category-master">
      <div className="category-header">
        <h2>Business Category Master</h2>
        <p>Manage business categories and classifications</p>
      </div>

      {/* Search and Filters */}
      <div className="controls-section">
        <div className="search-filter">
          <div className="search-box">
            <Search size={20} />
            <input
              type="text"
              placeholder="Search categories, codes, or descriptions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filters">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        
        <button
          className="btn-primary"
          onClick={() => setShowAddModal(true)}
        >
          <Plus size={20} />
          Add Category
        </button>
      </div>

      {/* Categories Table */}
      <div className="table-container">
        <table className="category-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Category Name</th>
              <th>Category Code</th>
              <th>Description</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.map(category => (
              <tr key={category.categoryid}>
                <td>{category.categoryid}</td>
                <td>
                  <div className="category-info">
                    <Briefcase size={16} />
                    {category.categoryname}
                  </div>
                </td>
                <td>
                  <div className="category-code">
                    {category.categorycode}
                  </div>
                </td>
                <td>
                  <div className="category-description">
                    {category.description || 'No description'}
                  </div>
                </td>
                <td>{getStatusBadge(category.status)}</td>
                <td>{new Date(category.created_at).toLocaleDateString()}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon"
                      onClick={() => openEditModal(category)}
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn-icon danger"
                      onClick={() => openDeleteModal(category)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {filteredCategories.length === 0 && (
          <div className="no-data">
            <p>No business categories found</p>
          </div>
        )}
      </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Business Category</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleAddCategory}>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  name="categoryname"
                  value={formData.categoryname}
                  onChange={handleInputChange}
                  className={formErrors.categoryname ? 'error' : ''}
                  placeholder="Enter category name"
                />
                {formErrors.categoryname && <span className="error-text">{formErrors.categoryname}</span>}
              </div>
              
              <div className="form-group">
                <label>Category Code *</label>
                <input
                  type="text"
                  name="categorycode"
                  value={formData.categorycode}
                  onChange={handleInputChange}
                  className={formErrors.categorycode ? 'error' : ''}
                  placeholder="Enter category code (max 10 chars)"
                  maxLength={10}
                />
                {formErrors.categorycode && <span className="error-text">{formErrors.categorycode}</span>}
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter category description"
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Adding...' : 'Add Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Business Category</h3>
              <button
                className="btn-icon"
                onClick={() => {
                  setShowEditModal(false);
                  resetForm();
                }}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleEditCategory}>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  name="categoryname"
                  value={formData.categoryname}
                  onChange={handleInputChange}
                  className={formErrors.categoryname ? 'error' : ''}
                  placeholder="Enter category name"
                />
                {formErrors.categoryname && <span className="error-text">{formErrors.categoryname}</span>}
              </div>
              
              <div className="form-group">
                <label>Category Code *</label>
                <input
                  type="text"
                  name="categorycode"
                  value={formData.categorycode}
                  onChange={handleInputChange}
                  className={formErrors.categorycode ? 'error' : ''}
                  placeholder="Enter category code (max 10 chars)"
                  maxLength={10}
                />
                {formErrors.categorycode && <span className="error-text">{formErrors.categorycode}</span>}
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter category description"
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    resetForm();
                  }}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : 'Update Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Delete Business Category</h3>
              <button
                className="btn-icon"
                onClick={() => setShowDeleteModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to delete the business category <strong>"{selectedCategory?.categoryname}"</strong>?</p>
              <p>This action cannot be undone.</p>
            </div>
            
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDeleteCategory}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessCategoryMaster; 